import * as d3 from "d3";
import { useEffect, useRef } from "react";
import "./PeopleGraph.css";

const data = {
  nodes: [
    { id: "Alice", description:"tmp" }, 
    { id: "Bob" }, 
    { id: "Charlie" }, 
    { id: "David" },
    { id: "Eve" }, 
    { id: "Frank" }, 
    { id: "Grace" }, 
    { id: "Hannah" },
    { id: "Isaac" }, 
    { id: "Jack" }, 
    { id: "Karen" }, 
    { id: "Leo" },
    { id: "Mia" }, 
    { id: "Nathan" }, 
    { id: "Olivia" }, 
    { id: "Paul" },
    { id: "Quinn" }, 
    { id: "Rachel" }, 
    { id: "Sam" }, 
    { id: "Tina" }
  ],
  links: [
    { source: "Alice", target: "Bob", relationship: "Friends", labelPosition: "above" },
    { source: "Alice", target: "Charlie", relationship: "Siblings", labelPosition: "same" },
    { source: "Bob", target: "David", relationship: "Colleagues", labelPosition: "same" },
    { source: "Charlie", target: "Eve", relationship: "Neighbors", labelPosition: "same" },
    { source: "David", target: "Frank", relationship: "Mentor", labelPosition: "below" },
    { source: "Eve", target: "Grace", relationship: "Partners", labelPosition: "same" },
    { source: "Frank", target: "Alice", relationship: "Teammates", labelPosition: "above" },
    { source: "Hannah", target: "Isaac", relationship: "Friends", labelPosition: "below" },
    { source: "Jack", target: "Karen", relationship: "Colleagues", labelPosition: "above" },
    { source: "Leo", target: "Mia", relationship: "Neighbors", labelPosition: "same" },
    { source: "Nathan", target: "Olivia", relationship: "Siblings", labelPosition: "below" },
    { source: "Paul", target: "Quinn", relationship: "Teammates", labelPosition: "above" },
    { source: "Rachel", target: "Sam", relationship: "Colleagues", labelPosition: "same" },
    { source: "Tina", target: "Alice", relationship: "Neighbors", labelPosition: "below" },
    { source: "Bob", target: "Leo", relationship: "Friends", labelPosition: "above" },
    { source: "Charlie", target: "Nathan", relationship: "Partners", labelPosition: "below" },
    { source: "David", target: "Mia", relationship: "Mentor", labelPosition: "same" },
    { source: "Eve", target: "Tina", relationship: "Friends", labelPosition: "above" },
    { source: "Frank", target: "Rachel", relationship: "Siblings", labelPosition: "below" },
    { source: "Hannah", target: "Paul", relationship: "Colleagues", labelPosition: "same" }
  ]
};

export default function PeopleGraph() {
  const svgRef = useRef();

  useEffect(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const nodeRadius = 40;

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    // ---------- ARROW MARKER ----------
    svg.append("defs")
      .append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", nodeRadius + 5)
      .attr("refY", 0)
      .attr("orient", "auto")
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .append("path")
      .attr("d", "M 0,-5 L 10,0 L 0,5")
      .attr("fill", "#999");

    // ---------- SIMULATION ----------
    const simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links).id(d => d.id).distance(150))
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(width / 2, height / 2));

    // ---------- LINKS ----------
    const link = svg.append("g")
      .selectAll("line")
      .data(data.links)
      .enter()
      .append("line")
      .attr("class", "link");

    // ---------- LINK LABELS ----------
    const linkLabels = svg.append("g")
      .selectAll("text")
      .data(data.links)
      .enter()
      .append("text")
      .attr("class", "edge-label")
      .text(d => d.relationship);

    // ---------- NODES ----------
    const node = svg.append("g")
      .selectAll("g")
      .data(data.nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .call(
        d3.drag()
          .on("start", dragStarted)
          .on("drag", dragged)
          .on("end", dragEnded)
      );

    node.append("circle")
      .attr("r", nodeRadius);

    node.append("text")
      .attr("dy", 5)
      .attr("text-anchor", "middle")
      .text(d => d.id);

    node.on("click", (event, d) => {
      d.fx = width / 2;
      d.fy = height / 2;
      simulation.alpha(1).restart();
    });

    // ---------- TICK ----------
    simulation.on("tick", () => {
      link.each(function(d) {
        let source = d.source;
        let target = d.target;
        let showArrow = true;

        if (d.labelPosition === "below") {
          [source, target] = [target, source]; // reverse arrow
        } else if (d.labelPosition === "same") {
          showArrow = false; // hide arrow
        }

        const dx = target.x - source.x;
        const dy = target.y - source.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        d3.select(this)
          .attr("x1", source.x + (dx / dist) * nodeRadius)
          .attr("y1", source.y + (dy / dist) * nodeRadius)
          .attr("x2", target.x - (dx / dist) * nodeRadius)
          .attr("y2", target.y - (dy / dist) * nodeRadius)
          .attr("marker-end", showArrow ? "url(#arrowhead)" : null);
      });

      linkLabels
        .attr("x", d => (d.source.x + d.target.x) / 2)
        .attr("y", d => {
          const base = (d.source.y + d.target.y) / 2;
          if (d.labelPosition === "above") return base - 10;
          if (d.labelPosition === "below") return base + 10;
          return base;
        });

      node.attr("transform", d => `translate(${d.x}, ${d.y})`);
    });

    function dragStarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragEnded(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => {
      svg.selectAll("*").remove();
      simulation.stop();
    };
  }, []);

  return <svg ref={svgRef} />;
}
